<template>
  <div class="mt-4">
    <!-- Selected Items Actions -->
    <div v-if="selectedItems.length > 0" class="mb-4 bg-white shadow sm:rounded-lg">
      <div class="px-4 py-4 sm:px-6">
        <div class="flex items-center justify-between">
          <span class="text-sm text-gray-700">
            {{ selectedItems.length }} items selected
          </span>
          <div class="flex space-x-3">
            <button
              @click="$emit('analyze-selected')"
              class="button-primary"
            >
              Analyze Selected
            </button>
            <button
              @click="$emit('mark-selected-important', selectedItems)"
              class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
            >
              Mark Important
            </button>
            <button
              @click="$emit('remove-selected-importance', selectedItems)"
              class="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-yellow-600 hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
            >
              Remove Importance
            </button>
            <button
              @click="$emit('delete-selected', selectedItems)"
              class="button-secondary"
            >
              Delete Selected
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="overflow-hidden bg-white shadow sm:rounded-md">
      <!-- Select All Checkbox -->
      <div class="px-4 py-3 border-b border-gray-200">
        <label class="flex items-center text-sm font-medium text-gray-700">
          <input
            type="checkbox"
            :checked="selectedItems.length === items.length && items.length > 0"
            :indeterminate="selectedItems.length > 0 && selectedItems.length < items.length"
            @change="toggleAllItems"
            class="h-4 w-4 mr-2 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          Select All Items
        </label>
      </div>
      
      <ul role="list" class="divide-y divide-gray-200">
        <ContentListItem
          v-for="(item, index) in items || []"
          :key="item.id"
          :item="item"
          :is-selected="selectedItems.includes(item.id)"
          :analyzing="analyzing"
          :find-article="findArticle"
          @toggle-selection="(event) => toggleSelection(event, index)"
          @analyze="$emit('analyze', $event)"
          @view-analysis="$emit('view-analysis', $event)"
          @toggle-importance="$emit('toggle-importance', $event)"
        />
      </ul>
    </div>

    <!-- Pagination -->
    <div class="mt-4 flex items-center justify-between">
      <div class="flex items-center">
        <span class="mr-2 text-sm text-gray-700">Show</span>
        <select
          v-model="selectedPageSize"
          class="rounded border-gray-300 text-base"
          @change="$emit('page-size-change', selectedPageSize)"
        >
          <option :value="10">10</option>
          <option :value="20">20</option>
          <option :value="50">50</option>
          <option :value="100">100</option>
        </select>
        <span class="ml-2 text-sm text-gray-700">entries</span>
      </div>
      
      <div class="flex items-center space-x-2">
        <button
          :disabled="pagination.page === 1"
          @click="$emit('page-change', pagination.page - 1)"
          class="px-3 py-1 rounded bg-gray-100 text-gray-700 disabled:opacity-50"
        >
          Previous
        </button>
        <span class="text-sm text-gray-700">
          Page {{ pagination.page }} of {{ pagination.totalPages }}
        </span>
        <button
          :disabled="pagination.page === pagination.totalPages"
          @click="$emit('page-change', pagination.page + 1)"
          class="px-3 py-1 rounded bg-gray-100 text-gray-700 disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import type { Content } from '@/types/content'
import type { Pagination } from '@/types/pagination'
import ContentListItem from './ContentListItem.vue'

const props = defineProps<{
  items: Content[]
  pagination: Pagination
  analyzing: string | null
  findArticle: (id: string) => Content | undefined
}>()

const emit = defineEmits<{
  (e: 'analyze', id: string): void
  (e: 'analyze-selected'): void
  (e: 'delete-selected', items: string[]): void
  (e: 'mark-selected-important', items: string[]): void
  (e: 'remove-selected-importance', items: string[]): void
  (e: 'view-analysis', item: Content): void
  (e: 'toggle-importance', item: Content): void
  (e: 'page-change', page: number): void
  (e: 'page-size-change', pageSize: number): void
}>()

const selectedItems = ref<string[]>([])
const selectedPageSize = ref(props.pagination.pageSize)
const lastSelectedIndex = ref<number | null>(null)

// Update selected page size when pagination changes
watch(() => props.pagination.pageSize, (newPageSize) => {
  selectedPageSize.value = newPageSize
})

function toggleAllItems(event: Event) {
  const target = event.target as HTMLInputElement
  if (target.checked) {
    selectedItems.value = props.items.map(item => item.id)
  } else {
    selectedItems.value = []
  }
  lastSelectedIndex.value = null
}

function toggleSelection(event: MouseEvent, index: number) {
  const id = props.items[index].id
  
  if (event.shiftKey && lastSelectedIndex.value !== null) {
    // Handle shift-click
    const start = Math.min(lastSelectedIndex.value, index)
    const end = Math.max(lastSelectedIndex.value, index)
    
    const itemsInRange = props.items.slice(start, end + 1).map(item => item.id)
    
    // If the last selected item was selected, select all in range
    // If it was deselected, deselect all in range
    const shouldSelect = selectedItems.value.includes(props.items[lastSelectedIndex.value].id)
    
    if (shouldSelect) {
      // Add all items in range that aren't already selected
      itemsInRange.forEach(itemId => {
        if (!selectedItems.value.includes(itemId)) {
          selectedItems.value.push(itemId)
        }
      })
    } else {
      // Remove all items in range
      selectedItems.value = selectedItems.value.filter(itemId => !itemsInRange.includes(itemId))
    }
  } else {
    // Handle normal click
    if (!selectedItems.value) {
      selectedItems.value = []
    }

    const idx = selectedItems.value.indexOf(id)
    if (idx === -1) {
      selectedItems.value.push(id)
    } else {
      selectedItems.value.splice(idx, 1)
    }
  }
  
  lastSelectedIndex.value = index
}
</script>

<style scoped>
.button-primary {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
  background-color: #4F46E5;
  border: 1px solid transparent;
  border-radius: 0.375rem;
}

.button-primary:hover {
  background-color: #4338CA;
}

.button-secondary {
  display: inline-flex;
  align-items: center;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  background-color: white;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.button-secondary:hover {
  background-color: #F9FAFB;
}

.input {
  padding: 0.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
}

.select {
  padding: 0.5rem;
  border: 1px solid #D1D5DB;
  border-radius: 0.375rem;
}
</style> 